// src/pages/LandingPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PandaAvatar from "../assets/pan-the-panda.png";
import PianoRollHQ from "../assets/piano-roll-card.svg"; // crisp SVG (2–3 black key pattern)
import { ensureGuestId, getUser, isLoggedIn, logout } from "../utils/auth";
import EmailLoginModal from "../components/EmailLoginModal";
import ParentsNoteModal from "../components/ParentsNoteModal";

export default function LandingPage() {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [parentsOpen, setParentsOpen] = useState(false);
  const [user, setUser] = useState(getUser());

  useEffect(() => {
    ensureGuestId();
  }, []);

  return (
    <div className="min-h-[100dvh] w-full flex flex-col bg-gradient-to-br from-yellow-100 to-blue-100 overflow-x-hidden">
      {/* Header */}
      <header className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
        <div className="w-full flex items-center justify-between">
          {!isLoggedIn() ? (
            <button
              onClick={() => setLoginOpen(true)}
              className="text-xs sm:text-sm bg-blue-500 text-white font-semibold py-2 px-3 sm:px-4 rounded shadow active:scale-[0.98] transition"
            >
              LOGIN
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-700 truncate max-w-[46vw]">
                Hi, {user?.email}
              </span>
              <button
                onClick={() => {
                  logout();
                  setUser(null);
                }}
                className="text-xs sm:text-sm px-2 py-1 rounded-md border bg-white/70"
              >
                Log out
              </button>
            </div>
          )}

          <div className="text-right leading-tight">
            <p className="text-[11px] sm:text-sm text-gray-700 font-medium">
              Piano For Everyone
            </p>
            <p className="text-[11px] sm:text-sm font-semibold">By Nishat Chadha</p>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 relative">
        {/* Centerpiece card */}
        <div
          className="
            absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-[92vw] sm:w-[88vw] md:w-[680px] lg:w-[580px] 2xl:w-[760px]
            bg-white/80 border border-gray-300 rounded-[28px] sm:rounded-[40px] shadow-md
            px-4 sm:px-8 py-5 sm:py-6
          "
        >
          <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-3 sm:gap-6 items-center justify-items-center">
            <img
              src={PandaAvatar}
              alt="Pan the Panda"
              className="mx-auto w-44 h-44 sm:w-56 sm:h-56 md:w-52 md:h-52 lg:w-56 lg:h-56 object-contain select-none"
              draggable="false"
            />

            {/* Right column: speech bubble + piano roll */}
            <div className="flex flex-col items-center gap-2 self-center">
              <div className="bg-yellow-100 rounded-lg px-4 py-2 shadow-inner text-black text-center text-base sm:text-lg leading-snug max-w-[75vw] sm:max-w-[340px]">
                Hi, welcome to Treble Town!
              </div>

              {/* High-quality SVG piano — ~25–35% larger + centered */}
              <img
                src={PianoRollHQ}
                alt="One octave piano: 7 white keys with 5 black keys (2–3 grouping)"
                className="block mx-auto w-64 sm:w-72 md:w-80 -mt-1 sm:-mt-2 drop-shadow select-none"
                draggable="false"
              />
            </div>
          </div>
        </div>

        {/* Parents note pill — fixed bottom-left on larger screens, solid white */}
        <button
          type="button"
          onClick={() => setParentsOpen(true)}
          className="hidden sm:flex items-center gap-2 fixed bottom-4 left-4 z-20 bg-white px-3 py-2 rounded-lg shadow text-left"
        >
          <p className="font-semibold text-purple-800 text-sm">
            <span className="mr-1">👨‍👩‍👧‍👦</span> For Grown-Ups – Note to Parents
          </p>
        </button>
      </main>

      {/* CTA area */}
      {/* Mobile: fixed bottom bar with safe-area padding */}
      <div className="sm:hidden fixed inset-x-0 bottom-0 z-10">
        <div className="backdrop-blur bg-white/70 border-t border-black/5 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => navigate("/intro")}
              className="w-full max-w-[520px] bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-3 rounded-full text-lg shadow-md active:scale-[0.99] transition"
              aria-label="Start the adventure"
            >
              START
            </button>
            <button
              onClick={() => navigate("/intro")}
              className="text-sm font-medium text-center underline underline-offset-4 text-purple-700"
            >
              🎵 Learn the Notes: Before the Adventure Begins!
            </button>

            {/* Parents link for mobile */}
            <button
              onClick={() => setParentsOpen(true)}
              className="text-[13px] text-purple-800 underline underline-offset-4"
            >
              👨‍👩‍👧‍👦 Note to Parents
            </button>
          </div>
        </div>
      </div>

      {/* Desktop/tablet: centered CTA below card */}
      <div className="hidden sm:flex flex-col items-center gap-3 mb-8 mt-6">
        <button
          onClick={() => navigate("/intro")}
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-2.5 px-8 rounded-full text-lg shadow-md active:scale-[0.99] transition"
        >
          START
        </button>
        <button
          onClick={() => navigate("/intro")}
          className="text-base font-medium text-center underline underline-offset-4 text-purple-700"
        >
          🎵 Learn the Notes: Before the Adventure Begins!
        </button>
      </div>

      {/* Modals */}
      <EmailLoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoggedIn={(u) => setUser(u)}
      />
      <ParentsNoteModal open={parentsOpen} onClose={() => setParentsOpen(false)} />
    </div>
  );
}
